import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Tabs, Button, Tag, Space, Timeline, Empty, Spin, Table, Descriptions,
  App as AntdApp, Modal, Input, Tooltip, Avatar,
} from 'antd';
import {
  Download, Upload, FileSignature, CheckCircle, XCircle, Clock, FileText,
  Archive, Trash2, Send, Link as LinkIcon, Eye, ChevronLeft, Unlink,
  History as HistoryIcon,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader, Card } from '@/components/common/PageHeader';
import { documentService, type Document, type DocumentStatus } from '@/services/documentService';
import { useAuthStore } from '@/store/authStore';
import { formatShortDateTime } from '@/utils/date';
import { UploadVersionModal } from '@/components/documents/UploadVersionModal';
import { SubmitApprovalModal } from '@/components/documents/SubmitApprovalModal';
import { LinkTaskModal } from '@/components/documents/LinkTaskModal';
// V1.0.65: 2 modal mới cho share + audit
import { ShareDocumentModal } from '@/components/documents/ShareDocumentModal';
import { DocumentAccessLogModal } from '@/components/documents/DocumentAccessLogModal';
import { PdfFullViewer } from '@/components/signing/PdfFullViewer';
import { OfficeViewer } from '@/components/common/OfficeViewer';  // V1.0.62: xem Word/Excel

const STATUS_CFG: Record<DocumentStatus, { label: string; color: string }> = {
  DRAFT: { label: 'Nháp', color: 'default' },
  PENDING_APPROVAL: { label: 'Chờ phê duyệt', color: 'processing' },
  APPROVED: { label: 'Đã phê duyệt', color: 'success' },
  SIGNED: { label: 'Đã ký', color: 'blue' },
  REJECTED: { label: 'Từ chối', color: 'error' },
  ARCHIVED: { label: 'Lưu trữ', color: 'default' },
};

// V1.0.65: 4-level visibility
const CONFIDENTIALITY_CFG: Record<string, { label: string; color: string; icon: string }> = {
  PUBLIC:       { label: 'Công khai', color: 'green',  icon: '🌐' },
  INTERNAL:     { label: 'Nội bộ',    color: 'blue',   icon: '🏢' },
  SHARED:       { label: 'Chia sẻ',   color: 'purple', icon: '🔗' },
  CONFIDENTIAL: { label: 'Mật',       color: 'red',    icon: '🔒' },
};

function formatBytes(bytes?: number): string {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const qc = useQueryClient();
  const currentUser = useAuthStore(s => s.user);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [linkTaskOpen, setLinkTaskOpen] = useState(false);
  // V1.0.65: state cho 2 modal mới
  const [shareOpen, setShareOpen] = useState(false);
  const [accessLogOpen, setAccessLogOpen] = useState(false);

  const { data: doc, isLoading } = useQuery({
    queryKey: ['document', id],
    queryFn: () => documentService.getById(id!),
    enabled: !!id,
  });

  if (isLoading || !doc) {
    return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;
  }

  const isOwner = doc.owner.id === currentUser?.id;
  // V1.0.65: Admin check để show button "Lịch sử truy cập"
  const isAdmin = currentUser?.roles?.includes('SYS_ADMIN') ?? false;
  const myPendingApproval = doc.approvals?.find(a =>
    a.approver.id === currentUser?.id && a.status === 'PENDING'
  );

  const handleDownload = async (versionNumber?: number) => {
    try {
      const blob = versionNumber !== undefined
        ? await documentService.downloadVersion(id!, versionNumber)
        : await documentService.download(id!);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const version = doc.versions?.find(v => v.versionNumber === (versionNumber ?? doc.currentVersion));
      a.href = url;
      a.download = version?.fileName ?? `${doc.docCode}.bin`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Lỗi tải file');
    }
  };

  const handleArchive = () => {
    Modal.confirm({
      title: 'Lưu trữ tài liệu?',
      content: 'Tài liệu sẽ bị ẩn khỏi danh sách chính. Có thể khôi phục lại sau.',
      okText: 'Lưu trữ',
      cancelText: 'Hủy',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await documentService.archive(id!);
          message.success('Đã lưu trữ');
          navigate('/documents');
        } catch (e: any) {
          message.error(e.response?.data?.message ?? 'Lỗi');
        }
      },
    });
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Xóa vĩnh viễn?',
      content: 'Tất cả phiên bản, lịch sử, phê duyệt sẽ bị xóa. Không khôi phục được.',
      okText: 'Xóa',
      cancelText: 'Hủy',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await documentService.delete(id!);
          message.success('Đã xóa');
          navigate('/documents');
        } catch (e: any) {
          message.error(e.response?.data?.message ?? 'Lỗi');
        }
      },
    });
  };

  const handleApprove = async () => {
    const comment = await new Promise<string | undefined>((resolve) => {
      let val = '';
      Modal.confirm({
        title: 'Phê duyệt tài liệu',
        content: (
          <Input.TextArea rows={3} placeholder="Ghi chú phê duyệt (tùy chọn)..."
            onChange={e => (val = e.target.value)} />
        ),
        okText: 'Phê duyệt',
        cancelText: 'Hủy',
        onOk: () => resolve(val || undefined),
        onCancel: () => resolve(undefined),
      });
    });
    if (comment === undefined && arguments.length === 0) return;
    try {
      await documentService.approve(id!, comment);
      message.success('Đã phê duyệt');
      qc.invalidateQueries({ queryKey: ['document', id] });
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Lỗi');
    }
  };

  const handleReject = async () => {
    let reason = '';
    Modal.confirm({
      title: 'Từ chối phê duyệt',
      content: (
        <Input.TextArea rows={3} placeholder="Lý do từ chối (bắt buộc)..." required
          onChange={e => (reason = e.target.value)} />
      ),
      okText: 'Từ chối',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: async () => {
        if (!reason.trim()) {
          message.warning('Vui lòng nhập lý do');
          return Promise.reject();
        }
        try {
          await documentService.reject(id!, reason);
          message.success('Đã từ chối');
          qc.invalidateQueries({ queryKey: ['document', id] });
        } catch (e: any) {
          message.error(e.response?.data?.message ?? 'Lỗi');
          throw e;
        }
      },
    });
  };

  const handleUnlinkTask = async (taskId: string) => {
    try {
      await documentService.unlinkTask(id!, taskId);
      message.success('Đã hủy liên kết');
      qc.invalidateQueries({ queryKey: ['document', id] });
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Lỗi');
    }
  };

  const statusCfg = STATUS_CFG[doc.status] ?? { label: doc.status, color: 'default' };
  const confCfg = CONFIDENTIALITY_CFG[doc.confidentiality] ?? { label: doc.confidentiality, color: 'default' };

  return (
    <>
      <PageHeader
        breadcrumb={<><Link to="/documents">Quản lý tài liệu</Link> / {doc.docCode}</>}
        title={doc.title}
        subtitle={
          <Space size={6}>
            <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
            <Tag color={confCfg.color}>{confCfg.label}</Tag>
            {doc.category && <Tag color={doc.category.color ?? 'default'}>{doc.category.name}</Tag>}
            <span style={{ fontSize: 12, color: '#7d8a98' }}>
              v{doc.currentVersion} · {doc.versions?.length ?? 0} phiên bản
            </span>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ChevronLeft size={14} />} onClick={() => navigate('/documents')}>Quay lại</Button>
            <Button icon={<Download size={14} />} onClick={() => handleDownload()}>
              Tải xuống
            </Button>
            {/* V1.0.65: Chia sẻ - chỉ owner */}
            {isOwner && (
              <Button icon={<LinkIcon size={14} />} onClick={() => setShareOpen(true)}>
                Chia sẻ
              </Button>
            )}
            {/* V1.0.65: Lịch sử truy cập - owner hoặc admin */}
            {(isOwner || isAdmin) && (
              <Button icon={<HistoryIcon size={14} />} onClick={() => setAccessLogOpen(true)}>
                Lịch sử
              </Button>
            )}
            {isOwner && doc.status === 'DRAFT' && (
              <Button type="primary" icon={<Send size={14} />}
                onClick={() => setApprovalOpen(true)}>
                Gửi phê duyệt
              </Button>
            )}
            {myPendingApproval && (
              <>
                <Button type="primary" icon={<CheckCircle size={14} />} onClick={handleApprove}>
                  Phê duyệt
                </Button>
                <Button danger icon={<XCircle size={14} />} onClick={handleReject}>
                  Từ chối
                </Button>
              </>
            )}
          </Space>
        }
      />

      <Card>
        <Tabs
          defaultActiveKey="info"
          items={[
            // ============ TAB 1: Thông tin ============
            {
              key: 'info',
              label: <span><FileText size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Thông tin</span>,
              children: (
                <div>
                  <Descriptions bordered size="small" column={2} labelStyle={{ width: 180, fontWeight: 500 }}>
                    <Descriptions.Item label="Mã tài liệu">
                      <code style={{ color: '#6699CC', fontWeight: 600 }}>{doc.docCode}</code>
                    </Descriptions.Item>
                    <Descriptions.Item label="Loại">{doc.category?.name ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="Chủ sở hữu">{doc.owner.fullName}</Descriptions.Item>
                    <Descriptions.Item label="Khoa/Phòng">
                      {/* V1.0.31: Hiển thị tất cả khoa được chia sẻ. Khoa primary in đậm. */}
                      {(() => {
                        const shared = doc.sharedWithDepartments ?? [];
                        const primary = doc.department;
                        // Nếu sharedWith chưa load (legacy) → fallback hiển thị primary
                        if (shared.length === 0) return primary?.name ?? '—';
                        // Sort: primary đứng đầu, sau đó alphabet
                        const sorted = [...shared].sort((a, b) => {
                          if (a.id === primary?.id) return -1;
                          if (b.id === primary?.id) return 1;
                          return a.name.localeCompare(b.name, 'vi');
                        });
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {sorted.map(d => (
                              <Tag
                                key={d.id}
                                color={d.id === primary?.id ? 'blue' : 'default'}
                                style={{ margin: 0, fontWeight: d.id === primary?.id ? 600 : 400 }}
                              >
                                {d.name}
                                {d.id === primary?.id && shared.length > 1 && (
                                  <span style={{ fontSize: 9, marginLeft: 4, opacity: 0.7 }}>(chủ quản)</span>
                                )}
                              </Tag>
                            ))}
                          </div>
                        );
                      })()}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tạo lúc">
                      {formatShortDateTime(doc.createdAt)} bởi {doc.createdBy.fullName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Cập nhật">
                      {formatShortDateTime(doc.updatedAt)}
                      {doc.updatedBy && ` bởi ${doc.updatedBy.fullName}`}
                    </Descriptions.Item>
                    <Descriptions.Item label="Ngày ban hành">
                      {doc.issuedDate ? new Date(doc.issuedDate).toLocaleDateString('vi-VN') : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Hiệu lực">
                      {doc.effectiveFrom && doc.effectiveTo
                        ? `${new Date(doc.effectiveFrom).toLocaleDateString('vi-VN')} — ${new Date(doc.effectiveTo).toLocaleDateString('vi-VN')}`
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tags" span={2}>
                      {doc.tags && doc.tags.length > 0
                        ? doc.tags.map(t => <Tag key={t}>{t}</Tag>)
                        : <span style={{ color: '#8a95a5' }}>—</span>}
                    </Descriptions.Item>
                    <Descriptions.Item label="Mô tả" span={2}>
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {doc.description ?? <span style={{ color: '#8a95a5' }}>—</span>}
                      </div>
                    </Descriptions.Item>
                  </Descriptions>

                  {isOwner && (
                    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e1e5eb' }}>
                      <Space>
                        <Button icon={<Archive size={14} />} onClick={handleArchive}>Lưu trữ</Button>
                        <Button danger icon={<Trash2 size={14} />} onClick={handleDelete}>Xóa</Button>
                      </Space>
                    </div>
                  )}
                </div>
              ),
            },

            // ============ TAB 2: Xem trước ============
            {
              key: 'preview',
              label: <span><Eye size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Xem trước</span>,
              children: (
                <DocumentPreview documentId={doc.id} fileName={doc.versions?.[0]?.fileName} fileMime={doc.versions?.[0]?.fileMime} />
              ),
            },

            // ============ TAB 3: Phiên bản ============
            {
              key: 'versions',
              label: <span><HistoryIcon size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Phiên bản ({doc.versions?.length ?? 0})</span>,
              children: (
                <div>
                  {isOwner && (
                    <div style={{ marginBottom: 12 }}>
                      <Button type="primary" icon={<Upload size={14} />}
                        onClick={() => setUploadOpen(true)}>
                        Upload phiên bản mới
                      </Button>
                    </div>
                  )}
                  <Table
                    dataSource={doc.versions ?? []}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    columns={[
                      {
                        title: '#',
                        dataIndex: 'versionNumber',
                        width: 70,
                        render: (n) => (
                          <Tag color={n === doc.currentVersion ? 'blue' : 'default'}>
                            v{n}{n === doc.currentVersion ? ' ●' : ''}
                          </Tag>
                        ),
                      },
                      {
                        title: 'File',
                        dataIndex: 'fileName',
                        render: (name, v) => (
                          <div>
                            <div style={{ fontSize: 13 }}>{name}</div>
                            <div style={{ fontSize: 11, color: '#8a95a5' }}>
                              {formatBytes(v.fileSize)} · SHA-256: <code style={{ fontSize: 10 }}>{v.fileHash?.slice(0, 12)}...</code>
                            </div>
                          </div>
                        ),
                      },
                      {
                        title: 'Ghi chú',
                        dataIndex: 'changeNote',
                        render: (note) => <span style={{ fontSize: 12 }}>{note ?? '—'}</span>,
                      },
                      {
                        title: 'Upload bởi',
                        dataIndex: 'uploadedBy',
                        width: 140,
                        render: (u, v) => (
                          <div style={{ fontSize: 12 }}>
                            <div>{u.fullName}</div>
                            <div style={{ color: '#8a95a5', fontSize: 11 }}>
                              {formatShortDateTime(v.uploadedAt)}
                            </div>
                          </div>
                        ),
                      },
                      {
                        title: 'Ký',
                        dataIndex: 'isSignedVersion',
                        width: 80,
                        render: (signed) => signed
                          ? <Tag color="blue" icon={<FileSignature size={10} />}>Đã ký</Tag>
                          : null,
                      },
                      {
                        title: '',
                        key: 'actions',
                        width: 90,
                        render: (_, v) => (
                          <Button size="small" icon={<Download size={12} />}
                            onClick={() => handleDownload(v.versionNumber)}>
                            Tải
                          </Button>
                        ),
                      },
                    ]}
                  />
                </div>
              ),
            },

            // ============ TAB 4: Phê duyệt ============
            {
              key: 'approvals',
              label: <span><CheckCircle size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Phê duyệt ({doc.approvals?.length ?? 0})</span>,
              children: (
                <div>
                  {(!doc.approvals || doc.approvals.length === 0) ? (
                    <Empty description="Chưa có yêu cầu phê duyệt nào">
                      {isOwner && doc.status === 'DRAFT' && (
                        <Button type="primary" icon={<Send size={14} />}
                          onClick={() => setApprovalOpen(true)}>
                          Gửi phê duyệt ngay
                        </Button>
                      )}
                    </Empty>
                  ) : (
                    <>
                      <div style={{ marginBottom: 12, fontSize: 12, color: '#7d8a98' }}>
                        Luồng phê duyệt: <b>{doc.approvalWorkflow === 'SEQUENTIAL' ? 'Tuần tự' : 'Song song'}</b>
                      </div>
                      <Timeline
                        items={doc.approvals.map(a => ({
                          color: a.status === 'APPROVED' ? 'green' :
                                 a.status === 'REJECTED' ? 'red' :
                                 a.status === 'PENDING' ? 'blue' : 'gray',
                          dot: a.status === 'APPROVED' ? <CheckCircle size={14} /> :
                               a.status === 'REJECTED' ? <XCircle size={14} /> :
                               <Clock size={14} />,
                          children: (
                            <div>
                              <div style={{ fontWeight: 500 }}>
                                #{a.approvalOrder}. {a.approver.fullName}
                                <Tag style={{ marginLeft: 8 }}
                                  color={a.status === 'APPROVED' ? 'success' :
                                         a.status === 'REJECTED' ? 'error' :
                                         a.status === 'PENDING' ? 'processing' : 'default'}>
                                  {a.status === 'APPROVED' ? 'Đã duyệt' :
                                   a.status === 'REJECTED' ? 'Từ chối' :
                                   a.status === 'PENDING' ? 'Đang chờ' : a.status}
                                </Tag>
                              </div>
                              {a.comment && (
                                <div style={{ fontSize: 12, color: '#4a5568', marginTop: 4,
                                  padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                                  "{a.comment}"
                                </div>
                              )}
                              {a.actedAt && (
                                <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
                                  {formatShortDateTime(a.actedAt)}
                                </div>
                              )}
                            </div>
                          ),
                        }))}
                      />
                    </>
                  )}
                </div>
              ),
            },

            // ============ TAB 5: Task liên kết ============
            {
              key: 'tasks',
              label: <span><LinkIcon size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Task liên kết ({doc.linkedTasks?.length ?? 0})</span>,
              children: (
                <div>
                  {isOwner && (
                    <div style={{ marginBottom: 12 }}>
                      <Button type="primary" icon={<LinkIcon size={14} />}
                        onClick={() => setLinkTaskOpen(true)}>
                        Liên kết task
                      </Button>
                    </div>
                  )}
                  {(!doc.linkedTasks || doc.linkedTasks.length === 0) ? (
                    <Empty description="Chưa liên kết với task nào" />
                  ) : (
                    <Table
                      dataSource={doc.linkedTasks}
                      rowKey="taskId"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Mã task',
                          dataIndex: 'taskCode',
                          width: 140,
                          render: (code, l) => (
                            <Link to={`/tasks/${l.taskId}`} style={{ fontFamily: 'monospace' }}>
                              {code}
                            </Link>
                          ),
                        },
                        { title: 'Tiêu đề', dataIndex: 'taskTitle' },
                        {
                          title: 'Loại',
                          dataIndex: 'linkType',
                          width: 120,
                          render: (t) => {
                            const map: Record<string, string> = {
                              RELATED: 'Liên quan', RESULT: 'Kết quả',
                              SOURCE: 'Nguồn', ATTACHMENT: 'Đính kèm',
                            };
                            return <Tag>{map[t] ?? t}</Tag>;
                          },
                        },
                        {
                          title: '',
                          width: 80,
                          render: (_, l) => isOwner ? (
                            <Button size="small" danger icon={<Unlink size={12} />}
                              onClick={() => handleUnlinkTask(l.taskId)}>
                              Hủy
                            </Button>
                          ) : null,
                        },
                      ]}
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Modals */}
      {uploadOpen && (
        <UploadVersionModal
          documentId={id!}
          currentVersion={doc.currentVersion}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            qc.invalidateQueries({ queryKey: ['document', id] });
          }}
        />
      )}
      {approvalOpen && (
        <SubmitApprovalModal
          documentId={id!}
          onClose={() => setApprovalOpen(false)}
          onSubmitted={() => {
            setApprovalOpen(false);
            qc.invalidateQueries({ queryKey: ['document', id] });
          }}
        />
      )}
      {linkTaskOpen && (
        <LinkTaskModal
          documentId={id!}
          onClose={() => setLinkTaskOpen(false)}
          onLinked={() => {
            setLinkTaskOpen(false);
            qc.invalidateQueries({ queryKey: ['document', id] });
          }}
        />
      )}
      {/* V1.0.65: Share + Access Log modals */}
      <ShareDocumentModal
        open={shareOpen}
        documentId={doc.id}
        documentTitle={doc.title}
        isOwner={isOwner}
        onClose={() => setShareOpen(false)}
      />
      <DocumentAccessLogModal
        open={accessLogOpen}
        documentId={doc.id}
        documentTitle={doc.title}
        onClose={() => setAccessLogOpen(false)}
      />
    </>
  );
}

// ============================================================
// Component xem trước PDF
// ============================================================
function DocumentPreview({ documentId, fileName, fileMime }: {
  documentId: string; fileName?: string; fileMime?: string;
}) {
  if (!fileName) {
    return <Empty description="Tài liệu chưa có file" />;
  }

  const lower = fileName.toLowerCase();
  const isPdf = fileMime?.includes('pdf') || lower.endsWith('.pdf');
  const isDocx = lower.endsWith('.docx') || fileMime?.includes('wordprocessingml');
  const isXlsx = lower.endsWith('.xlsx') || lower.endsWith('.xls') || lower.endsWith('.xlsm') || lower.endsWith('.csv')
    || fileMime?.includes('spreadsheetml') || fileMime?.includes('excel');

  // V1.0.62: Hỗ trợ xem Word (.docx) + Excel (.xlsx/.xls/.csv) trên trình duyệt
  if (isDocx || isXlsx) {
    return (
      // V1.0.64: Container fill chiều cao parent để viewer chiếm hết panel
      <div style={{ height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
        <OfficeViewer
          loadBlob={() => documentService.download(documentId)}
          fileName={fileName}
          fileMime={fileMime}
          height="100%"
        />
      </div>
    );
  }

  if (isPdf) {
    // PDF - dùng iframe với auth token qua blob URL
    return <PdfPreviewFrame documentId={documentId} fileName={fileName} />;
  }

  // Loại file khác (ảnh, đặc biệt .doc cũ) → fallback "Tải về"
  return (
    <div style={{ textAlign: 'center', padding: 40 }}>
      <FileText size={48} style={{ color: '#8a95a5', marginBottom: 12 }} />
      <div style={{ fontSize: 14, marginBottom: 8 }}>{fileName}</div>
      <div style={{ fontSize: 12, color: '#8a95a5', marginBottom: 16 }}>
        Loại file <b>{fileMime}</b> không hỗ trợ xem trước trên trình duyệt
      </div>
      <Button type="primary" icon={<Download size={14} />}
        onClick={async () => {
          const blob = await documentService.download(documentId);
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.click();
          URL.revokeObjectURL(url);
        }}>
        Tải về để xem
      </Button>
    </div>
  );
}

function PdfPreviewFrame({ documentId, fileName }: { documentId: string; fileName: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let currentUrl: string | null = null;
    documentService.download(documentId)
      .then(blob => {
        if (cancelled) return;
        currentUrl = URL.createObjectURL(blob);
        setBlobUrl(currentUrl);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [documentId]);

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
  if (!blobUrl) return <Empty description="Không tải được preview" />;

  // V1.0.47: Đổi iframe → PdfFullViewer (react-pdf canvas) để iPad/Safari xem được mọi trang
  // Trước đây iframe + blob URL chỉ render trang 1 trên iOS.
  return (
    <PdfFullViewer
      file={blobUrl}
      documentName={fileName}
      height="calc(100vh - 320px)"
    />
  );
}
